// Application Constants
// API Configuration
export const API_ENDPOINTS = {
    AUTH: {
        LOGIN: '/auth/login',
        REGISTER: '/auth/register',
        REFRESH: '/auth/refresh',
        LOGOUT: '/auth/logout',
        PROFILE: '/auth/profile',
    },
    USERS: {
        BASE: '/users',
        BY_ID: (id) => `/users/${id}`,
        UPLOAD_AVATAR: (id) => `/users/${id}/avatar`,
    },
    FILES: {
        UPLOAD: '/files/upload',
        DOWNLOAD: (id) => `/files/${id}/download`,
        DELETE: (id) => `/files/${id}`,
    },
    NOTIFICATIONS: {
        BASE: '/notifications',
        MARK_READ: (id) => `/notifications/${id}/read`,
        MARK_ALL_READ: '/notifications/mark-all-read',
    },
};
// HTTP Status Codes
export const HTTP_STATUS = {
    OK: 200,
    CREATED: 201,
    NO_CONTENT: 204,
    BAD_REQUEST: 400,
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    CONFLICT: 409,
    UNPROCESSABLE_ENTITY: 422,
    INTERNAL_SERVER_ERROR: 500,
    SERVICE_UNAVAILABLE: 503,
};
// Error Messages
export const ERROR_MESSAGES = {
    GENERIC: 'An unexpected error occurred',
    VALIDATION_FAILED: 'Validation failed',
    UNAUTHORIZED: 'Unauthorized access',
    FORBIDDEN: 'Access forbidden',
    NOT_FOUND: 'Resource not found',
    CONFLICT: 'Resource already exists',
    INTERNAL_ERROR: 'Internal server error',
    INVALID_CREDENTIALS: 'Invalid email or password',
    USER_NOT_FOUND: 'User not found',
    EMAIL_ALREADY_EXISTS: 'Email already exists',
    /**
     * Exact text `user.service.ts` throws when Keycloak's 409 is an email
     * conflict (or does not say which field collided). A username collision
     * keeps Keycloak's own sentence instead, so the web app can match this
     * literal and put it on the Email field without catching that case.
     */
    USER_EMAIL_CONFLICT: 'A user with this email address already exists',
    INVALID_TOKEN: 'Invalid or expired token',
    FILE_TOO_LARGE: 'File size exceeds limit',
    INVALID_FILE_TYPE: 'Invalid file type',
    UPLOAD_FAILED: 'File upload failed',
    DATABASE_ERROR: 'Database operation failed',
    REDIS_ERROR: 'Cache operation failed',
    EMAIL_SEND_FAILED: 'Failed to send email',
};
// Success Messages
export const SUCCESS_MESSAGES = {
    USER_CREATED: 'User created successfully',
    USER_UPDATED: 'User updated successfully',
    USER_DELETED: 'User deleted successfully',
    LOGIN_SUCCESS: 'Login successful',
    LOGOUT_SUCCESS: 'Logout successful',
    PASSWORD_UPDATED: 'Password updated successfully',
    EMAIL_SENT: 'Email sent successfully',
    FILE_UPLOADED: 'File uploaded successfully',
    FILE_DELETED: 'File deleted successfully',
    NOTIFICATION_SENT: 'Notification sent successfully',
    SETTINGS_UPDATED: 'Settings updated successfully',
};
// Validation Rules
export const VALIDATION_RULES = {
    PASSWORD: {
        MIN_LENGTH: 8,
        MAX_LENGTH: 128,
        REQUIRE_UPPERCASE: true,
        REQUIRE_LOWERCASE: true,
        REQUIRE_NUMBER: true,
        REQUIRE_SPECIAL: false,
    },
    EMAIL: {
        MAX_LENGTH: 255,
    },
    NAME: {
        MIN_LENGTH: 1,
        MAX_LENGTH: 50,
    },
    FILE: {
        MAX_SIZE: 10 * 1024 * 1024, // 10MB
        ALLOWED_TYPES: [
            'image/jpeg',
            'image/png',
            'image/gif',
            'image/webp',
            'application/pdf',
            'text/plain',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        ],
    },
    PAGINATION: {
        DEFAULT_PAGE: 1,
        DEFAULT_LIMIT: 20,
        MAX_LIMIT: 100,
    },
};
// Date/Time Formats
export const DATE_FORMATS = {
    ISO: 'YYYY-MM-DDTHH:mm:ss.SSSZ',
    DATE_ONLY: 'YYYY-MM-DD',
    TIME_ONLY: 'HH:mm:ss',
    DISPLAY: 'DD/MM/YYYY',
    DISPLAY_WITH_TIME: 'DD/MM/YYYY HH:mm',
    RELATIVE: 'relative',
};
// Environment Constants
export const ENVIRONMENTS = {
    DEVELOPMENT: 'development',
    STAGING: 'staging',
    PRODUCTION: 'production',
    TEST: 'test',
};
// Cache Keys
export const CACHE_KEYS = {
    USER_SESSION: (userId) => `user:session:${userId}`,
    USER_PROFILE: (userId) => `user:profile:${userId}`,
    FILE_METADATA: (fileId) => `file:metadata:${fileId}`,
    NOTIFICATION_COUNT: (userId) => `notification:count:${userId}`,
};
// Queue Names
export const QUEUE_NAMES = {
    EMAIL: 'email-queue',
    FILE_PROCESSING: 'file-processing-queue',
    NOTIFICATIONS: 'notifications-queue',
    AUDIT_LOGS: 'audit-logs-queue',
};
// JWT Configuration
export const JWT_CONFIG = {
    ACCESS_TOKEN_EXPIRY: '15m',
    REFRESH_TOKEN_EXPIRY: '7d',
    ALGORITHM: 'HS256',
};
// Rate Limiting
export const RATE_LIMITS = {
    LOGIN: {
        WINDOW_MS: 15 * 60 * 1000, // 15 minutes
        MAX_ATTEMPTS: 5,
    },
    API_GENERAL: {
        WINDOW_MS: 15 * 60 * 1000, // 15 minutes
        MAX_REQUESTS: 100,
    },
    FILE_UPLOAD: {
        WINDOW_MS: 60 * 1000, // 1 minute
        MAX_REQUESTS: 5,
    },
};
// Email Templates
export const EMAIL_TEMPLATES = {
    WELCOME: 'welcome',
    PASSWORD_RESET: 'password-reset',
    EMAIL_VERIFICATION: 'email-verification',
    NOTIFICATION: 'notification',
};
export * from './ach-return-codes.js';
export * from './audit-constants.js';
export * from './date.constants.js';
export * from './db-constants.js';
export * from './execution-detail-constants.js';
export * from './file-format-constants.js';
export * from './holiday.constants.js';
// Party constants
export * from './party-constants.js';
// Keycloak role names + route-guard role lists
export * from './roles.js';
//# sourceMappingURL=index.js.map